import React, { useState, useEffect } from 'react';
import { useTheme } from '../hooks/useTheme';

/* The app's one confirm (useConfirm), in Summit's own colors (styles/tokens.css): the surface, border, text and footer of
   its other windows (subs/SubUi.js ModalShell). Duncan 2026-10-26: "the dialogue box that comes up when you click delete
   does not match the theme of the website" -- it was the old navy palette (hooks/useTheme.js). The title and message are
   plain divs, not h3/p: App.css's dark-mode rules repaint any heading, paragraph and input inside a .modal-overlay slate
   (!important), so the typed confirmation's field has its own rule over them below. */
const CSS = `
.cfm-btn { height: 34px; padding: 0 16px; border-radius: 8px; font: 600 14px var(--font-sans); letter-spacing: -0.005em; cursor: pointer;
  transition: background .15s, border-color .15s, filter .15s; }
.cfm-cancel { color: var(--text-2); background: var(--bg-surface); border: 1px solid var(--border-default); box-shadow: var(--shadow-xs); }
.cfm-cancel:hover { background: var(--bg-surface-2); border-color: var(--border-strong); }
.cfm-ok { border: 1px solid transparent; }
.cfm-ok:hover:not(:disabled) { filter: brightness(1.06); }
.cfm-ok:disabled { opacity: .45; cursor: not-allowed; }
.cfm-input { width: 100%; height: 38px; padding: 0 12px; border-radius: 8px; box-sizing: border-box; font: 500 14.5px var(--font-mono);
  color: var(--text-1); background: var(--bg-surface); border: 1px solid var(--border-default); outline: none; transition: border-color .15s, box-shadow .15s; }
.cfm-input:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
.cfm-input.is-match { border-color: var(--success); }
.app.dark .modal-overlay .cfm-input, .theme-dark .modal-overlay .cfm-input {
  background: var(--bg-surface) !important; color: var(--text-1) !important; border-color: var(--border-default) !important; }
.app.dark .modal-overlay .cfm-input.is-match, .theme-dark .modal-overlay .cfm-input.is-match { border-color: var(--success) !important; }
`;

const ConfirmDialog = ({ open, title, message, confirmLabel, cancelLabel, onConfirm, onCancel, danger, requireTypedConfirmation }) => {
  const t = useTheme();
  const [typedValue, setTypedValue] = useState('');

  // Reset typed value when dialog opens/closes
  useEffect(() => {
    if (open) setTypedValue('');
  }, [open]);

  if (!open) return null;

  const needsTyping = !!requireTypedConfirmation;
  const typingMatch = needsTyping && typedValue.trim().toLowerCase() === requireTypedConfirmation.trim().toLowerCase();
  const canConfirm = !needsTyping || typingMatch;

  // The confirm button fills with danger red or Summit's green; on the dark theme those tokens are light, so its words
  // go dark (as AR's Add note does).
  const okBg = danger ? 'var(--danger)' : 'var(--accent)';
  const okInk = t.dark ? (danger ? 'oklch(0.2 0.04 25)' : 'oklch(0.15 0.01 100)') : '#fff';

  return (
    <div
      onClick={onCancel}
      // The overlay-level className is what triggers our app-wide
      // body:has(.modal-overlay) { overflow: hidden } scroll lock,
      // so include it here too even though the inline styles already
      // give it the same visual treatment.
      className="modal-overlay"
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        background: 'oklch(0.2 0.008 100 / 0.34)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'fadeIn 0.15s ease',
      }}
    >
      <style>{CSS}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-label={title || 'Are you sure?'}
        style={{
          width: '400px', maxWidth: '90vw', overflow: 'hidden',
          background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '14px',
          boxShadow: 'var(--shadow-md), 0 24px 60px -12px oklch(0.2 0.01 100 / 0.22)',
          fontFamily: 'var(--font-sans)', color: 'var(--text-1)', letterSpacing: '-0.005em',
          animation: 'slideUp 0.2s ease',
        }}
      >
        {/* Icon + Title */}
        <div style={{ padding: '20px 20px 18px' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '10px',
            background: danger ? 'var(--danger-soft)' : 'var(--accent-soft)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: '14px',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke={danger ? 'var(--danger)' : 'var(--accent)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div style={{ margin: '0 0 6px', fontSize: '17px', fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.012em' }}>
            {title || 'Are you sure?'}
          </div>
          <div style={{ margin: 0, fontSize: '14.5px', color: 'var(--text-2)', lineHeight: 1.5, textWrap: 'pretty' }}>
            {message || 'This action cannot be undone.'}
          </div>

          {/* Typed confirmation input */}
          {needsTyping && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ margin: '0 0 8px', fontSize: '14px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                Type <strong style={{
                  color: 'var(--text-1)', fontFamily: 'var(--font-mono)', background: 'var(--bg-surface-2)',
                  border: '1px solid var(--border-subtle)', padding: '1px 6px', borderRadius: '5px',
                }}>{requireTypedConfirmation}</strong> to confirm:
              </div>
              <input
                type="text"
                className={`cfm-input${typingMatch ? ' is-match' : ''}`}
                value={typedValue}
                onChange={(e) => setTypedValue(e.target.value)}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter' && canConfirm) onConfirm(); }}
                placeholder={requireTypedConfirmation}
              />
            </div>
          )}
        </div>

        {/* Buttons */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px',
          padding: '12px 20px 16px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)',
        }}>
          <button type="button" className="cfm-btn cfm-cancel" onClick={onCancel}>
            {cancelLabel || 'Cancel'}
          </button>
          <button
            type="button"
            className="cfm-btn cfm-ok"
            onClick={onConfirm}
            disabled={!canConfirm}
            style={{ background: okBg, color: okInk }}
          >
            {confirmLabel || 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
